import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { getLocalPath } from '../../utility'
import { fetchData } from '../../data'
import Loader from '../../../components/FundDetails/Modules/Shared/Loader.jsx'

function getTextValue(value) {
  if (typeof value === 'string') return value
  if (!value || typeof value !== 'object') return ''

  const nestedValue =
    value.content ?? value.value ?? value.html ?? value.text ?? ''

  return typeof nestedValue === 'string'
    ? nestedValue
    : getTextValue(nestedValue)
}

function getDocumentData(document) {
  if (!document) return null

  if (typeof document.getData === 'function') {
    const data = document.getData()
    if (data) return data
  }

  return document.model?.data ?? document
}

function getReferenceId(reference) {
  if (!reference) return null

  if (typeof reference === 'string') {
    return reference.split('/').filter(Boolean).pop()
  }

  const value = reference.$ref || reference.id || reference.uuid
  return value ? String(value).split('/').filter(Boolean).pop() : null
}

function getSelectedDisclosureDocument(page, component) {
  const models = component?.getModels?.() || {}
  const documentReference = models.document

  return documentReference && page?.getContent
    ? page.getContent(documentReference)
    : null
}

function getReferencedBlockFromPage(page, blockReference) {
  if (!page?.getContent || !blockReference) return null

  const candidates = [
    blockReference,
    blockReference.$ref,
    getReferenceId(blockReference),
  ].filter(Boolean)

  for (const candidate of candidates) {
    const content = page.getContent(candidate)
    if (content) return content
  }

  return null
}

function getDisclosureBlocks(documentData) {
  if (!documentData) return []

  const compound = documentData.ctidisclosureCompound
  const compoundBlocks = compound?.disclosureBlock

  if (Array.isArray(compoundBlocks)) {
    return compoundBlocks
  }

  if (Array.isArray(documentData.disclosureBlocks)) {
    return documentData.disclosureBlocks
  }

  return []
}

// A block entry can either point at another document via `disclosureBlock`
// (the legacy shape) or carry its content/reference inline on the entry
// itself. Normalize to the value loadBlockDocument expects.
function getBlockReference(block) {
  if (!block) return null
  return block.disclosureBlock ?? block
}

async function loadBlockDocument(page, blockReference) {
  const pageContent = getReferencedBlockFromPage(page, blockReference)

  if (pageContent) {
    return getDocumentData(pageContent)
  }

  const documentId = getReferenceId(blockReference)

  if (!documentId) {
    // No resolvable reference: the block likely carries its content inline
    // on the compound entry itself, so use it directly.
    return typeof blockReference === 'object' ? blockReference : null
  }

  return fetchData(`${getLocalPath()}/halapi/documents/${documentId}`)
}

export function Disclosure({ page, component, contentField = 'content' }) {
  const [documentData, setDocumentData] = useState(null)
  const [blockDocuments, setBlockDocuments] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const previewMode = Boolean(page?.isPreview?.())

  useEffect(() => {
    const selectedDocument = getSelectedDisclosureDocument(page, component)
    setDocumentData(getDocumentData(selectedDocument))
  }, [page, component, component?.model])

  useEffect(() => {
    let active = true

    const loadContent = async () => {
      if (!documentData) {
        setBlockDocuments([])
        setIsLoading(false)
        return
      }

      setIsLoading(true)

      const blocks = getDisclosureBlocks(documentData)

      const blockResults = await Promise.allSettled(
        blocks.map((block) => loadBlockDocument(page, getBlockReference(block)))
      )

      if (!active) return

      const resolvedBlocks = blockResults
        .map((result) => (result.status === 'fulfilled' ? result.value : null))
        .filter(Boolean)

      setBlockDocuments(resolvedBlocks)
      setIsLoading(false)
    }

    loadContent()

    return () => {
      active = false
    }
  }, [documentData, page])

  const items = useMemo(() => {
    if (!documentData) {
      return []
    }

    return blockDocuments
      .map((block, index) => {
        const rawValue =
          getTextValue(block?.[contentField]) ||
          getTextValue(block?.content) ||
          getTextValue(block?.text) ||
          getTextValue(block?.html) ||
          getTextValue(block?.description)

        if (!rawValue) return null

        return {
          key: block?._meta?.id || block?.id || `${index}`,
          html: rawValue.trim(),
        }
      })
      .filter(Boolean)
  }, [documentData, blockDocuments, contentField])

  if (isLoading) {
    return previewMode ? (
      <aside className="Content u-flexAuto u-paddingBottom0">
        <Loader />
      </aside>
    ) : null
  }

  if (!items.length && !previewMode) {
    return null
  }

  const heading =
    documentData?.heading || documentData?.ctidisclosureCompound?.heading

  return (
    <aside className="Content u-flexAuto u-paddingBottom0">
      {heading ? <h2>{heading}</h2> : null}

      {items.map((item) => (
        <div
          key={item.key}
          className="Disclaimer-text"
          dangerouslySetInnerHTML={{ __html: item.html }}
        />
      ))}
    </aside>
  )
}

Disclosure.propTypes = {
  page: PropTypes.object,
  component: PropTypes.object,
  contentField: PropTypes.string,
}

export default Disclosure
